import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

const EmpresaPage = () => {
  useSEO({
    title: 'Empresa - Limpieza Post Mortem Barcelona | BIOTRAUMA',
    description: 'Empresa especializada en limpieza post mortem en Barcelona. Protocolos certificados, EPIs homologados y coordinación con seguros y administraciones. Máxima confidencialidad.',
    keywords: 'empresa limpieza post mortem, Barcelona, protocolos certificados, EPIs homologados, confidencialidad',
    canonical: '/empresa',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        getOrganizationSchema(),
        getBreadcrumbSchema([
          { name: 'Inicio', url: '/' },
          { name: 'Empresa', url: '/empresa' }
        ])
      ]
    }
  });

  const values = [
    {
      icon: 'ri-shield-user-line',
      title: 'Confidencialidad Total',
      description: 'Trabajamos con absoluta discreción. Nuestros vehículos no llevan identificación externa y adaptamos horarios para evitar incomodidades. La privacidad de nuestros clientes es nuestra prioridad.',
      features: ['Vehículos sin rotulación', 'Horarios flexibles', 'Acceso discreto', 'Documentación confidencial']
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Trato Humano',
      description: 'Entendemos el momento difícil que atraviesan las familias. Nuestro equipo está formado específicamente en empatía y trato respetuoso, ofreciendo apoyo profesional sin perder la sensibilidad humana.',
      features: ['Equipo formado', 'Escucha activa', 'Respeto absoluto', 'Acompañamiento profesional']
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Protocolos Certificados',
      description: 'Aplicamos protocolos profesionales de limpieza post mortem con EPIs certificados y productos homologados. Cumplimos toda la normativa sanitaria vigente y emitimos certificados cuando se requieren.',
      features: ['EPIs certificados', 'Productos homologados', 'Normativa sanitaria', 'Certificados oficiales']
    },
    {
      icon: 'ri-team-line',
      title: 'Coordinación Profesional',
      description: 'Nos coordinamos con seguros, comunidades de vecinos, administradores de fincas e inmobiliarias. Facilitamos toda la documentación necesaria y gestionamos la relación con otros profesionales.',
      features: ['Gestión con seguros', 'Coordinación fincas', 'Presupuestos escritos', 'Documentación completa']
    }
  ];

  const timeline = [
    {
      year: '2018',
      title: 'Fundación',
      description: 'Inicio de actividad especializada en limpieza post mortem en Barcelona, detectando una necesidad de servicios profesionales y respetuosos en este ámbito.'
    },
    {
      year: '2019',
      title: 'Certificaciones',
      description: 'Obtención de certificaciones profesionales y establecimiento de protocolos específicos para limpieza tras fallecimiento según normativa sanitaria.'
    },
    {
      year: '2020',
      title: 'Expansión Provincial',
      description: 'Ampliación del servicio a toda la provincia de Barcelona y área metropolitana, incorporando nuevos equipos y vehículos especializados.'
    },
    {
      year: '2021',
      title: 'Acuerdos Institucionales',
      description: 'Establecimiento de acuerdos con compañías de seguros, administradores de fincas y servicios funerarios para ofrecer un servicio integral.'
    },
    {
      year: '2024',
      title: 'Líderes en Barcelona',
      description: 'Consolidación como referente en limpieza post mortem en Barcelona, con más de 500 intervenciones realizadas y valoración excelente de familias.'
    }
  ];

  const certifications = [
    {
      icon: 'ri-shield-check-line',
      title: 'Protocolos Sanitarios',
      description: 'Cumplimiento estricto de normativa sanitaria vigente en desinfección y saneamiento de espacios.'
    },
    {
      icon: 'ri-recycle-line',
      title: 'Gestión de Residuos',
      description: 'Gestor autorizado de residuos con documentación completa de trazabilidad y eliminación.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Seguros Profesionales',
      description: 'Cobertura de responsabilidad civil y seguros específicos para la actividad desarrollada.'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Formación Continua',
      description: 'Equipo en formación permanente en nuevas técnicas, protocolos y trato con familias.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-light to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                <span className="text-primary font-semibold text-sm">Sobre Nosotros</span>
              </div>
              
              <h1 className="font-display font-bold text-5xl lg:text-6xl text-dark mb-6 leading-tight">
                Profesionales en <span className="text-primary">limpieza post mortem</span> en Barcelona
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                BIOTRAUMA POST MORTEM es una empresa especializada en <strong>limpieza tras fallecimiento</strong> y <strong>desinfección profesional</strong> de inmuebles en Barcelona y provincia. Ofrecemos un servicio integral con máxima discreción, protocolos certificados y trato humano en momentos difíciles.
              </p>

              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Nuestro equipo está formado por profesionales especializados que comprenden la sensibilidad de estas situaciones. Trabajamos con <strong>equipos de protección individual certificados</strong>, productos homologados y cumpliendo toda la normativa sanitaria vigente. Nos coordinamos con seguros, administraciones y familias para facilitar todo el proceso. <Link to="/servicios" className="text-primary hover:underline cursor-pointer">Descubra todos nuestros servicios especializados</Link>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/servicios"
                  className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Nuestros Servicios
                  <i className="ri-arrow-right-line text-xl ml-3"></i>
                </Link>
                <Link
                  to="/contacto"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-dark text-dark font-semibold rounded-full hover:bg-dark hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Contactar
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/a65c3b2341f9411bee9f2e6921340c5a.jpg"
                  alt="Equipo profesional BIOTRAUMA"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="text-4xl font-display font-bold text-primary mb-1">500+</div>
                <div className="text-sm text-gray-600 font-medium">Intervenciones Realizadas</div>
              </div>
              
              <div className="absolute -top-8 -right-8 bg-primary rounded-2xl shadow-2xl p-6">
                <div className="text-4xl font-display font-bold text-white mb-1">24/7</div>
                <div className="text-sm text-white/90 font-medium">Disponibilidad</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Nuestros valores y compromiso
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Los pilares que guían nuestro trabajo diario y definen nuestra forma de actuar. <Link to="/" className="text-primary hover:underline cursor-pointer">Vuelva a la página principal</Link>.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border-2 border-gray-200 rounded-3xl p-8 hover:border-primary hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <i className={`${value.icon} text-3xl text-primary group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="font-display font-semibold text-2xl text-dark mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {value.description}
                </p>
                <ul className="grid grid-cols-2 gap-3">
                  {value.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <i className="ri-check-line text-primary text-lg mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 lg:py-32 bg-light">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Nuestra trayectoria
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Años de experiencia consolidando un servicio profesional y de confianza
            </motion.p>
          </div>

          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-primary/20"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative pl-24"
                >
                  {/* Node */}
                  <div className="absolute left-0 w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg shadow-primary/30">
                    <span className="font-display font-bold text-white text-lg">{item.year}</span>
                  </div>

                  {/* Content */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                    <h3 className="font-display font-semibold text-2xl text-dark mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Certificaciones y garantías
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Cumplimiento normativo y garantías profesionales que respaldan nuestro trabajo. <Link to="/servicios" className="text-primary hover:underline cursor-pointer">Conozca nuestros protocolos de trabajo</Link>.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <i className={`${cert.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-display font-semibold text-lg text-dark mb-3">
                  {cert.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {cert.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coordination Section */}
      <section className="py-20 lg:py-32 bg-dark text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display font-bold text-4xl lg:text-5xl mb-6">
                Coordinación con administraciones
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Facilitamos todo el proceso trabajando directamente con las partes implicadas. Emitimos <strong className="text-white">presupuestos por escrito</strong>, proporcionamos toda la documentación necesaria y nos coordinamos con comunidades de vecinos, administradores de fincas e inmobiliarias.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Si es necesario preparar el inmueble para entrega, venta o alquiler, podemos gestionar la coordinación con otros profesionales como pintores, electricistas o empresas de reparaciones. Nuestro objetivo es que usted tenga que preocuparse lo mínimo posible. <Link to="/faq" className="text-primary hover:underline cursor-pointer">Resuelva sus dudas sobre el proceso</Link>.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-300">Presupuestos detallados por escrito sin compromiso</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-300">Coordinación con administradores de fincas</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-300">Certificados oficiales de desinfección cuando se requieren</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-300">Documentación completa de la intervención realizada</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-dark-secondary rounded-3xl p-8 border border-gray-800">
                <h3 className="font-display font-semibold text-2xl mb-6">¿Necesita un presupuesto?</h3>
                <p className="text-gray-400 mb-8">
                  Solicite una valoración sin compromiso. Le atenderemos de inmediato y le proporcionaremos un presupuesto detallado por escrito.
                </p>
                <div className="space-y-4">
                  <a
                    href="tel:+34629501941"
                    className="flex items-center justify-center w-full px-6 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line text-xl mr-3"></i>
                    Llamar: +34 629 501 941
                  </a>
                  <a
                    href="https://wa.me/34629501941?text=Hola,%20necesito%20un%20presupuesto%20para%20limpieza%20post%20mortem"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full px-6 py-4 bg-white/10 text-white font-semibold rounded-full border-2 border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-xl mr-3"></i>
                    WhatsApp
                  </a>
                  <Link
                    to="/contacto"
                    className="flex items-center justify-center w-full px-6 py-4 bg-white/10 text-white font-semibold rounded-full border-2 border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Formulario de Contacto
                    <i className="ri-arrow-right-line text-xl ml-3"></i>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-light">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
          >
            Estamos aquí para ayudarle
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto"
          >
            Disponibles 24 horas, 7 días a la semana. Respuesta inmediata y valoración sin compromiso. <Link to="/contacto" className="text-primary hover:underline cursor-pointer">Contacte con nosotros ahora</Link>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/servicios"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Ver Servicios
              <i className="ri-arrow-right-line text-xl ml-3"></i>
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-dark text-dark font-semibold rounded-full hover:bg-dark hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Preguntas Frecuentes
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default EmpresaPage;
